/** A room's Everyone: the room talking together and with Maya, with the room's people, settings and groups beside it. */
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import type { HostEveryoneResponse, TalkGroupSets } from "../../shared/talk";
import { Button } from "../components/ui/Button";
import { Face } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { Sheet } from "../components/ui/Sheet";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { AskSheet } from "./AskSheet";
import { ChatList, RemoveSheet, useLineTools, useStick } from "./Chat";
import { Composer } from "./Composer";
import { Head, Poster, RoomGroups } from "./parts";
import { useHost } from "./useHost";
import { Nav, newCount, plainCount, Row } from "./phone";
import { announcementsPath, everyonePath, filesPath, questionsPath, roomHome } from "./places";
import { useTalkContext } from "./TalkLayout";
import { useRemote, useSeenUpTo, useTypers } from "./useTalk";
import { firstName, peopleWords } from "./when";
import { pinWords } from "../../shared/talk-words";

/** Who is here, this room's two switches, and its groups: the right side, or a sheet where there is no right side. */
/** The room's one switch: whether everyone talks together. */
function RoomSettings({ wid, data, onChange }: { wid: string; data: HostEveryoneResponse; onChange: () => void }) {
  const toast = useToast();
  const [chat, setChat] = useState(data.settings.everyoneChat);
  useEffect(() => setChat(data.settings.everyoneChat), [data.settings.everyoneChat]);

  const save = (everyoneChat: boolean) =>
    api.talk.settings(wid, { everyoneChat }).then(onChange, () => {
      setChat(!everyoneChat);
      toast({ text: "We couldn't save that. Try again.", icon: "info" });
    });

  return (
    <div className="set">
      <span>
        <b>Everyone chat</b>
        <small>{chat ? "On: everyone in the room can talk" : "Off: only announcements and circles"}</small>
      </span>
      <Toggle
        on={chat}
        label="Everyone chat"
        onChange={(v) => {
          setChat(v);
          void save(v);
        }}
      />
    </div>
  );
}

/** Who is here, this room's switch, and its groups: the right side, or a sheet where there is no right side. */
export function RoomSide({ wid, data, onChange }: { wid: string; data: HostEveryoneResponse; onChange: () => void }) {
  const [all, setAll] = useState(false);
  return (
    <>
      {data.presence.onlineCount > 0 ? (
        <>
          <h3>Online now · {data.presence.onlineCount}</h3>
          <div className="online">
            {data.presence.online.map((w, i) => (
              <span key={`${w.name}-${i}`} className="on-f" title={w.name}>
                <Face name={w.name} photo={w.photoUrl} size={34} />
                <i />
              </span>
            ))}
          </div>
        </>
      ) : null}
      <h3>This room</h3>
      <RoomSettings wid={wid} data={data} onChange={onChange} />
      <h3>Groups in this room</h3>
      <RoomGroups sets={data.groups as TalkGroupSets[]} all={all} onAll={setAll} room={{ id: wid, title: data.room.title }} onSent={onChange} />
    </>
  );
}

/** A room with Everyone chat off, on a phone: its places, groups and switches on one screen. */
export function RoomHub() {
  const { wid = "" } = useParams();
  const ctx = useTalkContext();
  const navigate = useNavigate();
  const { data, reload } = useRemote(`talk:everyone:${wid}`, () => api.talk.everyone(wid));
  const [all, setAll] = useState(false);
  if (!data) return <div className="hp" />;
  const r = data.room;
  return (
    <div className="hp">
      <Nav back="Rooms" title={r.title} sub={`${r.kind} · ${peopleWords(r.people)}`} />
      <div className="hp-scroll">
        <div className="hp-page">
          <section className="hp-card">
            {r.questionsWaiting !== null ? <Row icon="chat" label="Questions" end={r.questionsWaiting ? newCount(r.questionsWaiting) : undefined} onClick={() => navigate(questionsPath(wid))} /> : null}
            <Row icon="bell" label="Announcements" onClick={() => navigate(announcementsPath(wid))} />
            <Row icon="fold" label="Files" end={plainCount(r.filesCount)} onClick={() => navigate(filesPath(wid))} />
            <Row icon="plus" label="Start a circle" accent onClick={() => ctx.startCircle(wid)} />
          </section>
          <p className="hp-sub big">Groups in this room</p>
          <section className="hp-card hp-groups">
            <RoomGroups sets={data.groups as TalkGroupSets[]} all={all} onAll={setAll} room={{ id: wid, title: r.title }} onSent={() => void reload()} />
          </section>
          <p className="hp-sub big">This room</p>
          <section className="hp-card hp-sets">
            <RoomSettings wid={wid} data={data} onChange={() => void reload()} />
          </section>
        </div>
      </div>
    </div>
  );
}

/** The bare room address: it goes where the room opens, so a link or a bookmark never lands on an empty Everyone. */
export function RoomIndex() {
  const { wid = "" } = useParams();
  const { data, phase } = useRemote(`talk:room:${wid}`, () => api.talk.room(wid));
  if (data) return <Navigate to={roomHome(data)} replace />;
  return phase === "error" ? <Navigate to={everyonePath(wid)} replace /> : <main className="main" />;
}

export function EveryoneScreen() {
  const { wid = "" } = useParams();
  const ctx = useTalkContext();
  const phone = ctx.lay === "phone";
  const upright = ctx.lay === "upright";
  const toast = useToast();
  const typers = useTypers();
  const nameOf = useRef<(rid: string | undefined) => string | undefined>(() => undefined);
  const { data, phase, reload, setData, ping } = useRemote(`talk:everyone:${wid}`, () => api.talk.everyone(wid), `/talk/rooms/${encodeURIComponent(wid)}/ws`, (e) => typers.hear(e));
  nameOf.current = (rid) => data?.messages.find((m) => m.who.kind === "member" && m.who.registrantId === rid)?.who.name;
  useSeenUpTo(data ? `everyone:${wid}` : null, Math.max(0, ...(data?.messages.map((m) => m.createdAt) ?? [])), ctx.reloadRail);
  const me = useHost();
  const seen = useRef<{ wid: string; at: number | null } | null>(null);
  if (data && seen.current?.wid !== wid) seen.current = { wid, at: data.seenAt };
  const [asking, setAsking] = useState(false);
  const [info, setInfo] = useState(false);
  const [pin, setPin] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const onScroll = useStick(scroll, data?.messages.length ?? 0, wid);
  const tools = useLineTools({
    webinarId: wid,
    reload: () => void reload(),
    patch: (change, id) => setData((d) => (d ? { ...d, messages: d.messages.map((m) => (m.id === id ? change(m) : m)) } : d)),
  });
  useEffect(() => {
    if (data) ctx.reloadRail();
  }, [data, ctx.reloadRail]);

  if (!data) return phase === "error" ? <main className="main"><p className="empty-note">We couldn't open this room. Pick it again.</p></main> : <main className="main" />;
  if (!data.room.everyoneChat) return phone ? <RoomHub /> : <Navigate to={announcementsPath(wid)} replace />;

  const host = { name: me.name, photo: data.messages.find((m) => m.who.kind === "host")?.who.photoUrl ?? me.photo };
  const pinned = data.messages.filter((m) => m.pinned).toSorted((a, b) => a.createdAt - b.createdAt).at(-1);
  const send = async (out: { text: string; attachment: import("../../shared/types").Attachment | null }) => {
    setNotice(null);
    try {
      await api.talk.sendEveryone(wid, {
        body: out.text,
        attachments: out.attachment ? [out.attachment] : undefined,
        clientId: crypto.randomUUID(),
        replyTo: tools.replyTo?.id,
        pin: pin || undefined,
      });
      tools.setReplyTo(null);
      setPin(false);
      await reload();
    } catch (err) {
      setNotice(err instanceof Error && err.message ? err.message : "We couldn't send that. Try again.");
      throw err;
    }
  };
  const ask = async (q: { text: string; options: string[] }) => {
    await api.talk.sendEveryone(wid, { body: q.text, clientId: crypto.randomUUID(), poll: { question: q.text, options: q.options } });
    setAsking(false);
    toast({ text: "Sent", icon: "send" });
    await reload();
  };
  const replying = tools.replyTo ? { name: firstName(tools.replyTo.who.name), text: tools.replyTo.body, onClear: () => tools.setReplyTo(null) } : null;
  const people = peopleWords(data.room.people);
  const composer = (
    <Composer
      to="everyone in the room"
      phone={phone}
      host={host}
      send="Send"
      placeholder={phone ? "Write to everyone" : "Write to everyone in the room..."}
      away={asking}
      onAsk={() => setAsking(true)}
      onSend={send}
      onTyping={ping}
      notice={notice}
      replying={replying}
      checks={
        phone ? undefined : (
          <label className="cmp-pin">
            <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} /> Pin it
          </label>
        )
      }
    />
  );
  const sheets = (
    <>
      <AskSheet open={asking} to="everyone in the room" onClose={() => setAsking(false)} onSend={ask} />
      <RemoveSheet tools={tools} />
      <Sheet open={info} onClose={() => setInfo(false)} title={data.room.title}>
        <div className="sheet-b side-sheet">
          <RoomSide wid={wid} data={data} onChange={() => void reload()} />
        </div>
      </Sheet>
    </>
  );
  const list = (
    <ChatList
      className={phone ? "hp-chat" : "chat"}
      scroll={scroll}
      onScroll={onScroll}
      messages={data.messages}
      liveChats={data.liveChats}
      seenAt={seen.current?.at ?? null}
      tools={tools}
      phone={phone}
      host={host}
      members={data.room.people}
      typing={typers.who((rid) => nameOf.current(rid))}
      onWatch={() => undefined}
    />
  );

  if (phone) {
    return (
      <div className="hp">
        <Nav
          back="Rooms"
          title="Everyone"
          sub={data.room.title}
          right={
            <>
              <button type="button" className="hp-ib" aria-label="About this room" onClick={() => setInfo(true)}>
                <Icon name="info" />
              </button>
              <button type="button" className="hp-ib" aria-label="Start a circle" onClick={() => ctx.startCircle(wid)}>
                <Icon name="target" />
              </button>
            </>
          }
        />
        {pinned ? (
          <div className="hp-pin">
            <Icon name="pin" />
            <span>
              <b>{firstName(pinned.who.name)}:</b> {pinWords(pinned)}
            </span>
          </div>
        ) : null}
        {list}
        {composer}
        {sheets}
      </div>
    );
  }
  return (
    <>
      <main className="main">
        <Head
          pic={<Poster url={data.room.posterUrl} className="mh-pic" />}
          title="Everyone in the room"
          sub={`${data.room.title} · ${people}`}
          onInfo={upright ? () => setInfo(true) : undefined}
          infoLabel="About this room"
          right={
            <>
              <Button icon="target" onClick={() => ctx.startCircle(wid)}>
                Start a circle
              </Button>
            </>
          }
        />
        {pinned ? (
          <div className="pinbar">
            <Icon name="pin" />
            <span>
              <b>{firstName(pinned.who.name)}:</b> {pinWords(pinned)}
            </span>
            {pinned.hearts ? (
              <span className="react">
                <Icon name="heart" /> {pinned.hearts}
              </span>
            ) : null}
          </div>
        ) : null}
        {list}
        {composer}
      </main>
      <aside className="side">
        <RoomSide wid={wid} data={data} onChange={() => void reload()} />
      </aside>
      {sheets}
    </>
  );
}
